import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  CircularProgress,
  Alert,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  Campaign as CallIcon,
  Business as ConsortiumIcon,
  Person as PersonIcon,
  Send as SendIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import { axiosService } from 'shared/helpers/axios';
import { debounce } from 'lodash';

function SuggestEntityDialog({ open, onClose, user, onDone }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedTab, setSelectedTab] = useState(0); // 0: All, 1: Calls, 2: Consortia, 3: Users
  const [suggesting, setSuggesting] = useState({});
  const [suggested, setSuggested] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [suggestionMessage, setSuggestionMessage] = useState('');

  const [suggestedEntities, setSuggestedEntities] = useState([]);

  React.useEffect(() => {
    if (user && open) {
        fetchSuggestedEntitiesForUser(user.id).then(entities => {
        setSuggestedEntities(entities);
      });
    }
  } , [user, open]);  

  // Debounced search function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const performSearch = useCallback(
    debounce(async (query, tab) => {
      if (!query.trim()) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      
      try {
        const results = [];

        // Determine which endpoints to search based on tab
        const searchCalls = tab === 0 || tab === 1;
        const searchConsortia = tab === 0 || tab === 2;
        const searchUsers = tab === 0 || tab === 3;

        const promises = [];

        if (searchCalls) {
          promises.push(
            axiosService.get(`/funding/calls/?search=${encodeURIComponent(query)}&page_size=10`)
              .catch(() => ({ data: [] }))
          );
        } else {
          promises.push(Promise.resolve({ data: [] }));
        }

        if (searchConsortia) {
          promises.push(
            axiosService.get(`/consortium/consortiums/?search=${encodeURIComponent(query)}&page_size=10`)
              .catch(() => ({ data: [] }))
          );
        } else {
          promises.push(Promise.resolve({ data: [] }));
        }

        if (searchUsers) {
          promises.push(
            axiosService.get(`/users/?search=${encodeURIComponent(query)}&page_size=10`)
              .catch(() => ({ data: [] }))
          );
        } else {
          promises.push(Promise.resolve({ data: [] }));
        }

        const [callsRes, consortiaRes, usersRes] = await Promise.all(promises);

        // Process funding calls
        const calls = Array.isArray(callsRes.data) 
          ? callsRes.data 
          : callsRes.data?.results || [];
        
        calls.forEach(call => {
          results.push({
            id: call.id,
            type: 'call',
            title: call.title,
            description: call.description || call.call_type || 'Funding Call',
            subtitle: call.agency?.name || call.program?.name || '',
            deadline: call.stage1_deadline || call.deadline,
          });
        });

        // Process consortia
        const consortia = Array.isArray(consortiaRes.data)
          ? consortiaRes.data
          : consortiaRes.data?.results || [];
        
        consortia.forEach(consortium => {
          results.push({
            id: consortium.id,
            type: 'consortium',
            title: consortium.name,
            description: consortium.description || 'Consortium',
            subtitle: `${consortium.memberships?.length || 0} members • ${consortium.status || 'Active'}`,
            avatar: consortium.logo || null, 
          });
        });

        // Process users (exclude the current recipient)
        const users = Array.isArray(usersRes.data)
          ? usersRes.data
          : usersRes.data?.results || [];
        
        users.forEach(u => {
          if (u.id !== user?.id) {
            results.push({
              id: u.id,
              type: 'user',
              title: `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.username,
              description: u.position || u.bio || 'User',
              subtitle: u.affiliation || u.department || '',
              avatar: u.avatar || null,
            });
          }
        });
        
        console.log("*** suggested entities ",  suggestedEntities);
        setSearchResults(results);

      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300),
    [user?.id]
  );

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
    performSearch(query, selectedTab);
  };

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue);
    if (searchQuery.trim()) {
      performSearch(searchQuery, newValue);
    }
  };


  const fetchSuggestedEntitiesForUser = async (userId) => {
    try {
      const response = await axiosService.get(`/interact/suggestions/suggested-entities-for-user/${userId}/`);
      return response.data; // Assuming the response contains a list of suggested entities
    } catch (error) {
      console.error('Error fetching suggested entities for user:', error);
      return [];
    }
  };  


  const getSuggestEndpoint = (type, id) => {
    const endpoints = {
      consortium: `/interact/suggestions/by_target/consortium/${id}/`,
      project: `/interact/suggestions/by_target/project/${id}/`,
      call: `/interact/suggestions/by_target/call/${id}/`,
      organization: `/interact/suggestions/by_target/organization/${id}/`,
      user: `/interact/suggestions/by_target/user/${id}/`,
    };
    return endpoints[type] || '/interact/suggestions/';
  };

  const handleSuggest = async (entity) => {
    setSuggesting((prev) => ({ ...prev, [entity.id]: true }));
    setError(null);
    setSuccess(null);

    try {
      const suggestionData = {
        recipient: user?.id,
        target_type: entity.type,
        target_id: entity.id,
        kind: ['call', 'consortium'].includes(entity.type) ? 'collaboration' : entity.type === 'user' ? 'contact' : 'other',
        body: suggestionMessage.trim() || `I thought you might be interested in: ${entity.title || entity.name || entity.username || ''}`,
        status: 'open'
      };

      await axiosService.post(getSuggestEndpoint(entity.type, entity.id), suggestionData);

      setSuggested((prev) => ({ ...prev, [entity.id]: true }));
      onDone?.();
      setSuccess(`Successfully suggested "${entity.title}" to ${user.name || user.username}!`);
      
      // Auto-clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error sending suggestion:', err);
      setError(err.response?.data?.detail || 'Failed to send suggestion. Please try again.');
    } finally {
      setSuggesting((prev) => ({ ...prev, [entity.id]: false }));
    }
  };

  const handleClose = (e) => {
    setSearchQuery('');
    setSearchResults([]);
    setSuggestionMessage('');
    setSuggested({});
    setError(null);
    setSuccess(null);
    setSelectedTab(0);
    onClose(e);
  };

  const getEntityIcon = (type) => {
    switch (type) {
      case 'call':
        return <CallIcon />;
      case 'consortium':
        return <ConsortiumIcon />;
      case 'user':
        return <PersonIcon />;
      default:
        return <SearchIcon />;
    }
  };


  const SUGGEST_COLOR = 'secondary.main'; // You can change this to any color you prefer

  const getEntityColor = (type) => {
    // switch (type) {
    //   case 'call':
    //     return 'primary.light';
    //   case 'consortium':
    //     return 'secondary.dark';
    //   case 'user':
    //     return 'primary.main';
    //   default:
    //     return 'grey.400';
    // }

    return 'secondary.main';
  };

  const filteredResults = searchResults.filter(result => {
    if (selectedTab === 0) return true; // All
    if (selectedTab === 1) return result.type === 'call';
    if (selectedTab === 2) return result.type === 'consortium';
    if (selectedTab === 3) return result.type === 'user';
    return true;
  });

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      onClick={(e) =>  {e.stopPropagation()}}
      sx={{zIndex: 1600}}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SendIcon sx={{ color: SUGGEST_COLOR }} />
            <Typography variant="h6">
              Suggest to {user?.first_name || user?.username || 'User'}
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Search Bar */}
        <TextField
          fullWidth
          placeholder="Search for funding calls, consortia, or users..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        {/* Tabs for filtering */}
        <Tabs
          value={selectedTab}
          onChange={handleTabChange}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="All" />
          <Tab label="Funding Calls" />
          <Tab label="Consortia" />
          <Tab label="Users" />
        </Tabs>

        {/* Optional message */}
        <TextField
          fullWidth
          multiline
          rows={2}
          placeholder="Add a personal message (optional)..."
          value={suggestionMessage}
          onChange={(e) => setSuggestionMessage(e.target.value)}
          sx={{ mb: 2 }}
        />

        {/* Search Results */}
        {isSearching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredResults.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              {searchQuery.trim() ? 'No results found. Try a different search.' : 'Start typing to search...'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {filteredResults.map((entity) => (

              // console.log("*** entity",  entity),

              <ListItem
                key={entity.id}
                sx={{
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 1,
                  bgcolor: 'background.paper',
                }}
              >
                <ListItemAvatar>
                  <Avatar src={ entity.avatar } sx={{ bgcolor: getEntityColor(entity.type), color: 'white' }}>
                    {getEntityIcon(entity.type)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1">
                        {entity.title}
                      </Typography>
                      <Chip
                        label={entity.type}
                        size="small"
                        sx={{ height: 20, textTransform: 'capitalize' }}
                      />
                    </Box>
                  }
                  secondary={
                    <>
                      <Typography variant="body2" color="text.secondary">
                        {entity.description}
                      </Typography>
                      {entity.subtitle && (
                        <Typography variant="caption" color="text.secondary">
                          {entity.subtitle}
                        </Typography>
                      )}
                    </>
                  }
                />
                <Tooltip
                  title={
                    suggested[entity.id]
                      ? 'Suggestion sent'
                      : suggesting[entity.id]
                      ? 'Sending...'
                      : 'Send suggestion'
                  }
                >
                  <span>
                    <Button
                      variant={suggesting[entity.id] || suggested[entity.id]  || suggestedEntities.includes(entity.id)? 'outlined' : 'contained'}
                      color={suggesting[entity.id] || suggested[entity.id]  || suggestedEntities.includes(entity.id) ? 'success' : 'primary'}
                      size="small"
                      onClick={() => handleSuggest(entity)}
                      disabled={suggesting[entity.id] || suggested[entity.id]  || suggestedEntities.includes(entity.id)}
                      startIcon={
                        suggesting[entity.id] ? (
                          <CircularProgress size={16} />
                        ) : suggested[entity.id] ? (
                          <CheckIcon />
                        ) : (
                          <SendIcon />
                        )
                      }
                      sx={{ minWidth: 100 }}
                    >
                      {suggested[entity.id] || suggestedEntities.includes(entity.id) ? 'Suggested' : 'Suggest'}
                    </Button>
                  </span>
                </Tooltip>
              </ListItem>
            ))}
          </List>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default SuggestEntityDialog;
